<template>
  <section class="lc" aria-label="Дела о претензиях">
    <header class="lc-head">
      <div>
        <h2 class="lc-title">
          Дела о претензиях
          <span :class="['lc-dot', `lc-dot--${summary.light || 'grey'}`]" :title="lightTitle(summary.light)"></span>
        </h2>
        <p class="lc-lead">
          Каждая претензия — наша заказчику, заказчика нам, перевозчику — ведётся как дело со сроком по договору.
          Дела по срывам и простоям система открывает сама.
          <router-link class="lc-link" to="/knowledge?a=legal-cases">Как это работает</router-link>
        </p>
        <div v-if="data && data.summary" class="lc-stats">
          <span v-if="summary.money_ours" class="lc-stat lc-stat--gold" title="Наши претензии заказчику в работе"><b>{{ money(summary.money_ours) }}</b> к получению</span>
          <span v-if="summary.money_risk" class="lc-stat lc-stat--risk" title="Претензии к нам и перевыставления в работе"><b>{{ money(summary.money_risk) }}</b> под риском удержания</span>
        </div>
      </div>
      <div class="lc-actions">
        <button type="button" class="lc-btn" @click="openRegister">Пришла претензия</button>
        <button type="button" class="lc-btn lc-btn--ghost" @click="toggleState">{{ state === 'open' ? 'Показать закрытые' : 'Только открытые' }}</button>
      </div>
    </header>

    <ProcessStory
      title="Светофор юристов"
      :flow="data && data.available !== false ? summary.flow || null : null"
      :items="data?.items || []"
      :generated-at="data?.generated_at || ''"
      :failed="Boolean(error)"
      :active-light="light"
      :money="money"
      :now="nowMs"
      @light="light = $event"
      @open="openCase"
    />

    <ProcessFlow
      v-if="summary.flow"
      title="Путь дел"
      :flow="summary.flow"
      :items="data?.items || []"
      :active="stage"
      :due-text="due"
      @stage="stage = $event"
      @open="openCase"
    />

    <nav class="lc-lights" aria-label="Фильтр по светофору">
      <button v-if="light" type="button" :class="['lc-chip', 'is-active', `lc-chip--${light}`]" @click="light = ''">{{ lightTitle(light) }} ✕</button>
      <button
        v-if="summary.flow && summary.flow.stuck_count"
        type="button"
        :class="['lc-chip', 'lc-chip--yellow', { 'is-active': stuckOnly }]"
        title="Ход за нами, а дело не двигалось — срок задаётся в Пульте правил"
        @click="stuckOnly = !stuckOnly"
      >
        ⏸ Зависло <b>{{ summary.flow.stuck_count }}</b>
      </button>
      <button v-if="stage" type="button" class="lc-chip is-active" @click="stage = ''">Этап: {{ stageTitle }} ✕</button>
      <label class="lc-sort">
        <span class="lc-visually-hidden">Порядок</span>
        <select v-model="sort" aria-label="Порядок дел">
          <option v-for="s in SORTS" :key="s.key" :value="s.key">{{ s.title }}</option>
        </select>
      </label>
      <input v-model="query" class="lc-search" type="search" placeholder="Рейс, клиент, вид дела…" aria-label="Поиск по делам" />
    </nav>

    <p v-if="error" class="lc-error" role="alert">
      {{ data ? 'Не удалось обновить — ниже данные последнего успешного обновления.' : 'Не удалось загрузить дела.' }}
      <span class="lc-muted">{{ error }}</span>
      <button type="button" class="lc-link" @click="load">Повторить</button>
    </p>
    <p v-if="!data && error" class="lc-empty">Пока связи нет, показатели скрыты — ноль здесь был бы неправдой.</p>
    <p v-else-if="data && data.available === false" class="lc-empty">Раздел готовится к запуску — дела появятся после обновления базы.</p>
    <div v-else-if="pending && !rows.length" class="lc-skeleton"><span v-for="n in 3" :key="n"></span></div>
    <p v-else-if="!rows.length" class="lc-empty"><span aria-hidden="true">🌿</span> Дел нет — всё спокойно.</p>

    <TransitionGroup v-else tag="ul" name="lc-row" class="lc-list">
      <li
        v-for="(c, idx) in visible"
        :key="c.id"
        :class="['lc-card', `lc-card--${c.light}`]"
        :style="{ '--i': Math.min(idx, 12) }"
        tabindex="0"
        role="button"
        :aria-label="`Дело №${c.id}: ${c.title}. ${c.light_title}. ${c.deviation || c.status_title}`"
        @click="openCase(c.id)"
        @keydown.enter.prevent="openCase(c.id)"
        @keydown.space.prevent="openCase(c.id)"
      >
        <span class="lc-card__icon" aria-hidden="true">{{ KIND_ICONS[c.kind] || '•' }}</span>
        <div class="lc-card__main">
          <div class="lc-card__top">
            <b>№{{ c.id }} · {{ c.title }}</b>
            <span class="lc-badge">{{ c.status_title }}</span>
          </div>
          <div class="lc-card__meta">
            {{ c.kind_title }}<template v-if="c.trip_number"> · рейс {{ c.trip_number }}</template><template v-if="c.client"> · {{ c.client }}</template>
          </div>
          <div v-if="c.deviation" :class="['lc-card__dev', `is-${c.light}`]">{{ c.deviation }}<template v-if="c.consequence"> — <span>{{ c.consequence }}</span></template></div>
          <div v-if="c.next_step" class="lc-card__next"><b>Что сделать:</b> {{ c.next_step }}</div>
          <div class="lc-card__flow">
            <span :class="['lc-light', `lc-light--${c.light}`]"><i aria-hidden="true"></i>{{ c.light_title }}</span>
            <span :class="['lc-owner', `lc-owner--${c.owner}`]">{{ c.responsible || c.owner_title }}</span>
            <span v-if="c.stuck" class="lc-stuck">⏸ Зависло: {{ stalledText(c.stalled_days) }}</span>
            <span v-else-if="c.stalled_days" class="lc-muted">{{ stalledText(c.stalled_days) }}</span>
          </div>
        </div>
        <div class="lc-card__side">
          <div v-if="c.amount" class="lc-card__money">{{ money(c.amount) }}</div>
          <div :class="['lc-card__due', `is-${c.light}`]">{{ c.outcome_title || due(c) }}</div>
        </div>
      </li>
    </TransitionGroup>
    <div v-if="rows.length > visible.length" class="lc-more">
      <button type="button" class="lc-btn lc-btn--ghost" @click="limit += PAGE">Показать ещё {{ Math.min(PAGE, rows.length - visible.length) }} из {{ rows.length - visible.length }}</button>
    </div>

    <teleport to="body">
      <div v-if="openId" class="lc-backdrop" @mousedown.self="closeCase">
        <aside class="lc-drawer" role="dialog" aria-modal="true" aria-label="Дело о претензии">
          <button type="button" class="lc-close" aria-label="Закрыть" @click="closeCase">×</button>
          <div v-if="!detail" class="lc-skeleton"><span v-for="n in 4" :key="n"></span></div>
          <template v-else>
            <p class="lc-drawer__kind">{{ KIND_ICONS[detail.kind] }} {{ detail.kind_title }}</p>
            <h3 class="lc-drawer__title">№{{ detail.id }} · {{ detail.title }}</h3>
            <div class="lc-drawer__row">
              <span class="lc-badge">{{ detail.status_title }}</span>
              <span :class="['lc-card__due', `is-${detail.light}`]">{{ detail.outcome_title || due(detail) }}</span>
            </div>
            <ProcessPath v-if="detail.path" :steps="detail.path" />
            <ol v-if="detail.deviation || detail.consequence || detail.responsible" class="lc-story">
              <li v-if="detail.deviation" :class="`is-${detail.light}`"><span>Что не так</span>{{ detail.deviation }}</li>
              <li v-if="detail.consequence"><span>На что влияет</span>{{ detail.consequence }}</li>
              <li v-if="detail.responsible"><span>Кто действует</span>{{ detail.responsible }}</li>
            </ol>
            <p v-if="detail.stuck" class="lc-stuckline">⏸ Дело зависло: ход за нами, {{ stalledText(detail.stalled_days) }}. Примите решение или отметьте шаг.</p>
            <dl class="lc-facts">
              <template v-if="detail.trip_number"><dt>Рейс</dt><dd><router-link v-if="detail.cargo_trip_id" :to="`/trips/${detail.cargo_trip_id}`">{{ detail.trip_number }}</router-link><span v-else>{{ detail.trip_number }}</span></dd></template>
              <template v-if="detail.amount"><dt>Сумма</dt><dd>{{ money(detail.amount) }}</dd></template>
              <template v-if="detail.counterparty"><dt>Сторона</dt><dd>{{ detail.counterparty }}</dd></template>
              <template v-if="detail.received_at"><dt>Получена</dt><dd>{{ when(detail.received_at) }}</dd></template>
              <template v-if="detail.sent_at"><dt>Отправлено</dt><dd>{{ when(detail.sent_at) }}</dd></template>
            </dl>
            <p v-if="detail.basis" class="lc-basis">{{ detail.basis }}</p>
            <p v-if="detail.next_step" class="lc-next"><b>Следующий шаг.</b> {{ detail.next_step }}</p>

            <div v-if="detail.actions && detail.actions.length" class="lc-decide">
              <template v-if="canDecide">
                <textarea v-model="comment" rows="2" maxlength="4000" placeholder="Комментарий: на чём основано решение"></textarea>
                <div class="lc-decide__btns">
                  <button v-for="a in detail.actions" :key="a.key" type="button"
                          :class="['lc-btn', 'lc-btn--small', { 'lc-btn--ghost': a.key === 'write_off' }]"
                          :disabled="busy" @click="decide(a)">{{ a.title }}</button>
                </div>
                <p v-if="actionError" class="lc-error">{{ actionError }}</p>
              </template>
              <p v-else class="lc-muted">Решения по делу принимают юристы и руководители.</p>
            </div>

            <h4 class="lc-h4">Журнал дела</h4>
            <ol class="lc-log">
              <li v-for="(e, i) in detail.events" :key="i"><span class="lc-muted">{{ when(e.at) }} · {{ e.who }}</span><br />{{ e.text }}</li>
            </ol>
          </template>
        </aside>
      </div>

      <div v-if="registerOpen" class="lc-backdrop" @mousedown.self="registerOpen = false">
        <form class="lc-dialog" role="dialog" aria-modal="true" aria-labelledby="lc-reg-title" @submit.prevent="submitRegister">
          <h3 id="lc-reg-title">Пришла претензия</h3>
          <p class="lc-muted">Срок ответа система запустит от даты получения. Не ответили мотивированно в срок — претензия считается признанной.</p>
          <div class="lc-seg" role="radiogroup" aria-label="Что пришло">
            <button type="button" :class="['lc-seg__btn', { 'is-active': form.kind === 'customer_claim' }]" @click="form.kind = 'customer_claim'">Претензия заказчика</button>
            <button type="button" :class="['lc-seg__btn', { 'is-active': form.kind === 'quality_report' }]" @click="form.kind = 'quality_report'">Отчёт по качеству</button>
          </div>
          <label for="lc-trip">Номер рейса</label>
          <input id="lc-trip" v-model="form.tripNumber" inputmode="numeric" placeholder="например, 50753495" />
          <label for="lc-date">Когда получена</label>
          <input id="lc-date" v-model="form.receivedAt" type="date" />
          <label for="lc-amount">Сумма, ₽ <span class="lc-muted">(если есть)</span></label>
          <input id="lc-amount" v-model="form.amount" inputmode="decimal" />
          <label for="lc-who">От кого</label>
          <input id="lc-who" v-model="form.counterparty" placeholder="например, АБ ИнБев Эфес" />
          <label for="lc-basis">Суть претензии</label>
          <textarea id="lc-basis" v-model="form.basis" rows="3" maxlength="4000" placeholder="Основание, пункт договора, что требуют"></textarea>
          <p v-if="formError" class="lc-error">{{ formError }}</p>
          <div class="lc-dialog__actions">
            <button type="button" class="lc-btn lc-btn--ghost" :disabled="busy" @click="registerOpen = false">Отмена</button>
            <button type="submit" class="lc-btn" :disabled="busy">{{ busy ? 'Сохраняем…' : 'Открыть дело' }}</button>
          </div>
        </form>
      </div>
    </teleport>
  </section>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, reactive, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { caseAction, fetchLegalCase, fetchLegalCases, registerClaim } from '@/backend/legalApi'
import { KIND_ICONS, LIGHTS, dueText, filterCases, validateClaim } from '@/pages/claims/legalLogic.mjs'
import { formatMoney } from '@/pages/claims/claimsLogic.mjs'
import ProcessFlow from '@/components/process/ProcessFlow.vue'
import ProcessPath from '@/components/process/ProcessPath.vue'
import ProcessStory from '@/components/process/ProcessStory.vue'
import { SORTS, sortItems, stalledText } from '@/components/process/processFlow.mjs'

const REFRESH_MS = 3 * 60 * 1000
const data = ref(null)
const pending = ref(false)
const error = ref('')
const state = ref('open')
const light = ref('')
const query = ref('')
const stage = ref('')
const stuckOnly = ref(false)
const sort = ref('urgency')
const PAGE = 30
const limit = ref(PAGE)
const nowMs = ref(Date.now())
const route = useRoute()
const router = useRouter()
const openId = ref(null)
const detail = ref(null)
const comment = ref('')
const busy = ref(false)
const actionError = ref('')
const registerOpen = ref(false)
const formError = ref('')
const today = () => new Date().toISOString().slice(0, 10)
const form = reactive({ kind: 'customer_claim', tripNumber: '', receivedAt: today(), amount: '', counterparty: '', basis: '' })
let timer = null

const summary = computed(() => data.value?.summary || {})
const canDecide = computed(() => Boolean(data.value?.can_decide || detail.value?.can_decide))
const rows = computed(() => sortItems(filterCases(data.value?.items || [],
  { light: light.value, query: query.value, stage: stage.value, stuck: stuckOnly.value }), sort.value))
const visible = computed(() => rows.value.slice(0, limit.value))

// Контекст сохраняется в адресе: вернулись из рейса — те же фильтры и то же дело (стандарт, п. 4).
const Q = { light: 'lc_light', stage: 'lc_stage', query: 'lc_q', sort: 'lc_sort', stuck: 'lc_stuck', open: 'lc_case' }
function readContext() {
  const q = route?.query || {}
  light.value = String(q[Q.light] || '')
  stage.value = String(q[Q.stage] || '')
  query.value = String(q[Q.query] || '')
  sort.value = SORTS.some((s) => s.key === q[Q.sort]) ? String(q[Q.sort]) : 'urgency'
  stuckOnly.value = q[Q.stuck] === '1'
  return Number(q[Q.open]) || null
}
function writeContext() {
  if (!router || !route) return
  const next = { ...route.query }
  const set = (k, v) => { if (v) next[k] = v; else delete next[k] }
  set(Q.light, light.value); set(Q.stage, stage.value); set(Q.query, query.value.trim())
  set(Q.sort, sort.value === 'urgency' ? '' : sort.value); set(Q.stuck, stuckOnly.value ? '1' : '')
  set(Q.open, openId.value ? String(openId.value) : '')
  router.replace({ query: next }).catch(() => {})
}
watch([light, stage, query, sort, stuckOnly, openId], () => { limit.value = PAGE; writeContext() })
const stageTitle = computed(() => (summary.value.flow?.stages || []).find((s) => s.key === stage.value)?.title || stage.value)
const money = (v) => formatMoney(v)
const due = (c) => dueText(c.days_left, c.due)
const lightTitle = (key) => (LIGHTS.find((l) => l.key === key) || {}).title || ''
const when = (iso) => (iso ? new Date(iso).toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—')

async function load() {
  pending.value = true
  nowMs.value = Date.now()
  try {
    data.value = await fetchLegalCases(state.value)
    error.value = ''
  } catch (e) {
    error.value = e?.response?.data?.detail || e?.message || 'нет связи'
  } finally {
    pending.value = false
  }
}

function toggleState() {
  state.value = state.value === 'open' ? 'all' : 'open'
  load()
}

async function openCase(id) {
  openId.value = id
  detail.value = null
  comment.value = ''
  actionError.value = ''
  try {
    detail.value = await fetchLegalCase(id)
  } catch (e) {
    actionError.value = e?.response?.data?.detail || e?.message || 'Не удалось открыть дело'
    detail.value = { events: [], actions: [] }
  }
}

function closeCase() {
  if (busy.value) return
  openId.value = null
}

async function decide(a) {
  actionError.value = ''
  if (a.needs_comment && !comment.value.trim()) {
    actionError.value = 'Добавьте комментарий: на чём основано решение'
    return
  }
  busy.value = true
  try {
    detail.value = await caseAction(openId.value, a.key, comment.value)
    comment.value = ''
    load()
  } catch (e) {
    actionError.value = e?.response?.data?.detail || e?.message || 'Не удалось сохранить решение'
  } finally {
    busy.value = false
  }
}

function openRegister() {
  formError.value = ''
  Object.assign(form, { kind: 'customer_claim', tripNumber: '', receivedAt: today(), amount: '', counterparty: '', basis: '' })
  registerOpen.value = true
}

async function submitRegister() {
  const check = validateClaim(form)
  if (!check.ok) {
    formError.value = check.error
    return
  }
  busy.value = true
  try {
    const created = await registerClaim(check.payload)
    registerOpen.value = false
    await load()
    if (created?.id) openCase(created.id)
  } catch (e) {
    formError.value = e?.response?.data?.detail || e?.message || 'Не удалось открыть дело'
  } finally {
    busy.value = false
  }
}

function onKey(e) {
  if (e.key !== 'Escape') return
  if (registerOpen.value) registerOpen.value = false
  else if (openId.value) closeCase()
}

let clock = null
onMounted(() => {
  const caseId = readContext()
  load()
  if (caseId) openCase(caseId)
  timer = setInterval(load, REFRESH_MS)
  clock = setInterval(() => { nowMs.value = Date.now() }, 60 * 1000)
  window.addEventListener('keydown', onKey)
})
onBeforeUnmount(() => {
  clearInterval(timer)
  clearInterval(clock)
  window.removeEventListener('keydown', onKey)
})
</script>

<style scoped>
.lc { --gold: #f2b705; --gold-2: #ffe08a; --ink: #1f2430; --muted: #6b7280; --line: #e7e5df;
  --red: #dc2626; --yellow: #d97706; --green: #16a34a; --grey: #9ca3af;
  padding: 18px 20px; border-radius: 20px; background: #fff; border: 1px solid var(--line); color: var(--ink);
  box-shadow: 0 18px 40px -30px rgba(31, 36, 48, .45); display: flex; flex-direction: column; gap: 14px; animation: lc-in .35s ease both; }
.lc-head { display: flex; justify-content: space-between; gap: 14px; flex-wrap: wrap; align-items: flex-start; }
.lc-title { margin: 0; font-size: 20px; display: flex; align-items: center; gap: 8px; }
.lc-lead { margin: 4px 0 0; color: var(--muted); font-size: 13.5px; max-width: 760px; line-height: 1.5; }
.lc-link { color: var(--ink); border: 0; background: none; padding: 0; font: inherit; cursor: pointer; text-decoration: underline; text-decoration-color: var(--gold); }
.lc-stats { display: flex; gap: 8px; flex-wrap: wrap; margin-top: 8px; }
.lc-stat { padding: 4px 11px; border-radius: 999px; font-size: 12.5px; background: #f3f1ea; }
.lc-stat b { margin-right: 4px; }
.lc-stat--gold { background: #fff6d6; color: #7a5a00; }
.lc-stat--risk { background: #fef2f2; color: #991b1b; }
.lc-actions { display: flex; gap: 8px; flex-wrap: wrap; }
.lc-dot { width: 12px; height: 12px; border-radius: 50%; background: var(--grey); box-shadow: 0 0 0 4px rgba(156, 163, 175, .18); }
.lc-dot--red { background: var(--red); box-shadow: 0 0 0 4px rgba(220, 38, 38, .18); animation: lc-pulse 1.6s ease-in-out infinite; }
.lc-dot--yellow { background: var(--yellow); box-shadow: 0 0 0 4px rgba(217, 119, 6, .18); }
.lc-dot--green { background: var(--green); box-shadow: 0 0 0 4px rgba(22, 163, 74, .18); }

.lc-btn { display: inline-flex; align-items: center; padding: 9px 15px; border-radius: 999px; border: 0; font: inherit; font-size: 13.5px;
  font-weight: 650; cursor: pointer; color: var(--ink); background: linear-gradient(135deg, var(--gold-2), var(--gold)); transition: transform .2s, box-shadow .2s; }
.lc-btn:hover:not(:disabled) { transform: translateY(-1px); box-shadow: 0 10px 22px -12px rgba(242, 183, 5, .95); }
.lc-btn:disabled { opacity: .6; cursor: default; }
.lc-btn--ghost { background: #f7f5ef; border: 1px solid var(--line); }
.lc-btn--small { padding: 7px 12px; font-size: 12.5px; }

.lc-lights { display: flex; gap: 6px; flex-wrap: wrap; align-items: center; }
.lc-chip { padding: 6px 12px; border-radius: 999px; border: 1px solid var(--line); background: #fff; font: inherit; font-size: 12.5px; cursor: pointer; }
.lc-chip b { margin-left: 4px; }
.lc-chip--red b { color: var(--red); } .lc-chip--yellow b { color: var(--yellow); } .lc-chip--green b { color: var(--green); } .lc-chip--grey b { color: var(--muted); }
.lc-chip.is-active { background: var(--ink); color: #fff; border-color: var(--ink); }
.lc-chip.is-active b { color: var(--gold-2); }
.lc-search { margin-left: auto; min-width: 240px; padding: 8px 14px; border-radius: 999px; border: 1px solid var(--line); font: inherit; font-size: 13.5px; }
.lc-search:focus { outline: 2px solid var(--gold-2); border-color: var(--gold); }

.lc-list { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 8px; }
.lc-card { display: grid; grid-template-columns: auto 1fr auto; gap: 12px; align-items: start; padding: 12px 14px; border-radius: 14px;
  border: 1px solid var(--line); border-left: 4px solid var(--grey); cursor: pointer; transition: transform .2s, box-shadow .2s, background .2s; }
.lc-card:hover { transform: translateY(-1px); background: #fbfaf6; box-shadow: 0 10px 22px -18px rgba(31, 36, 48, .55); }
.lc-card--red { border-left-color: var(--red); } .lc-card--yellow { border-left-color: var(--yellow); } .lc-card--green { border-left-color: var(--green); }
.lc-card__icon { font-size: 20px; }
.lc-card__top { display: flex; gap: 8px; flex-wrap: wrap; align-items: center; }
.lc-card__meta { color: var(--muted); font-size: 12.5px; margin-top: 2px; }
.lc-card__next { font-size: 12.5px; margin-top: 6px; color: #374151; line-height: 1.4; max-width: 720px; }
.lc-card__side { text-align: right; white-space: nowrap; }
.lc-card__money { font-weight: 700; }
.lc-card__due { font-size: 12.5px; margin-top: 2px; color: var(--muted); }
.lc-card__due.is-red { color: var(--red); font-weight: 700; } .lc-card__due.is-yellow { color: var(--yellow); font-weight: 600; }
.lc-card__flow { display: flex; gap: 8px; align-items: center; flex-wrap: wrap; margin-top: 6px; font-size: 12px; }
.lc-owner { padding: 1px 8px; border-radius: 999px; background: #f3f1ea; }
.lc-owner--we { background: #fff6d6; color: #7a5a00; font-weight: 600; }
.lc-stuck { color: var(--yellow); font-weight: 700; }
.lc-stuckline { margin: 0; padding: 8px 12px; border-radius: 12px; background: #fff7ed; color: #9a3412; font-size: 13px; }
.lc-card__dev { margin-top: 6px; font-size: 13px; font-weight: 650; line-height: 1.4; color: #374151; }
.lc-card__dev span { font-weight: 400; color: #4b5563; }
.lc-card__dev.is-red { color: var(--red); } .lc-card__dev.is-yellow { color: #b45309; }
.lc-light { display: inline-flex; align-items: center; gap: 5px; padding: 1px 8px; border-radius: 999px; background: #f3f1ea; font-weight: 600; }
.lc-light i { width: 7px; height: 7px; border-radius: 50%; background: var(--grey); }
.lc-light--red { background: #fef2f2; color: #991b1b; } .lc-light--red i { background: var(--red); }
.lc-light--yellow { background: #fff7ed; color: #9a3412; } .lc-light--yellow i { background: var(--yellow); }
.lc-light--green { background: #f0fdf4; color: #166534; } .lc-light--green i { background: var(--green); }
.lc-card:focus-visible { outline: 2px solid var(--gold); outline-offset: 2px; }
.lc-row-enter-active { transition: opacity .35s ease, transform .35s cubic-bezier(.2, .7, .2, 1); transition-delay: calc(var(--i, 0) * 30ms); }
.lc-row-leave-active { transition: opacity .2s ease, transform .2s ease; }
.lc-row-enter-from { opacity: 0; transform: translateY(8px); } .lc-row-leave-to { opacity: 0; transform: translateX(-12px); }
.lc-row-move { transition: transform .35s cubic-bezier(.2, .7, .2, 1); }
.lc-more { display: flex; justify-content: center; }
.lc-sort select { padding: 7px 12px; border-radius: 999px; border: 1px solid var(--line); background: #fff; font: inherit; font-size: 12.5px; cursor: pointer; }
.lc-visually-hidden { position: absolute; width: 1px; height: 1px; overflow: hidden; clip: rect(0 0 0 0); white-space: nowrap; }
.lc-story { list-style: none; margin: 0; padding: 10px 12px; border-radius: 12px; background: #fbfaf6; display: grid; gap: 6px; }
.lc-story li { display: grid; grid-template-columns: 110px 1fr; gap: 10px; font-size: 13.5px; line-height: 1.45; }
.lc-story li span { color: var(--muted); font-size: 12.5px; }
.lc-story li.is-red { color: var(--red); font-weight: 650; } .lc-story li.is-yellow { color: #b45309; font-weight: 650; }
.lc-list { position: relative; }
.lc-badge { padding: 2px 9px; border-radius: 999px; font-size: 11.5px; background: #f3f1ea; color: var(--ink); }

.lc-empty { padding: 22px; text-align: center; color: var(--muted); border-radius: 14px; background: #fbfaf7; margin: 0; }
.lc-error { color: var(--red); margin: 0; font-size: 13px; }
.lc-muted { color: var(--muted); font-size: 12.5px; }
.lc-skeleton { display: flex; flex-direction: column; gap: 8px; }
.lc-skeleton span { height: 48px; border-radius: 12px; background: linear-gradient(90deg, #f2f0ea, #faf9f5, #f2f0ea); background-size: 200% 100%; animation: lc-shimmer 1.4s linear infinite; }

.lc-backdrop { position: fixed; inset: 0; z-index: 2000; display: flex; justify-content: flex-end; align-items: stretch;
  background: rgba(21, 23, 29, .5); backdrop-filter: blur(3px); animation: lc-fade .2s ease both; }
.lc-drawer { position: relative; width: min(560px, 100%); height: 100%; overflow-y: auto; padding: 26px 24px; background: #fff; color: var(--ink);
  box-shadow: -30px 0 60px -30px rgba(0, 0, 0, .5); animation: lc-slide .28s cubic-bezier(.2, .7, .2, 1) both; display: flex; flex-direction: column; gap: 10px; }
.lc-close { position: absolute; top: 12px; right: 14px; border: 0; background: #f3f1ea; width: 32px; height: 32px; border-radius: 50%; font-size: 20px; cursor: pointer; }
.lc-drawer__kind { margin: 0; color: var(--muted); font-size: 13px; }
.lc-drawer__title { margin: 0; font-size: 20px; padding-right: 36px; }
.lc-drawer__row { display: flex; gap: 10px; align-items: center; flex-wrap: wrap; }
.lc-facts { display: grid; grid-template-columns: auto 1fr; gap: 4px 14px; margin: 4px 0; font-size: 13.5px; }
.lc-facts dt { color: var(--muted); } .lc-facts dd { margin: 0; }
.lc-basis { margin: 0; padding: 10px 12px; border-radius: 12px; background: #fbfaf6; font-size: 13.5px; line-height: 1.5; }
.lc-next { margin: 0; font-size: 13.5px; line-height: 1.5; padding: 10px 12px; border-radius: 12px; background: #fff8e1; }
.lc-decide { display: flex; flex-direction: column; gap: 8px; }
.lc-decide textarea, .lc-dialog input, .lc-dialog textarea { padding: 9px 12px; border-radius: 12px; border: 1px solid var(--line); font: inherit; font-size: 13.5px; }
.lc-decide__btns { display: flex; gap: 6px; flex-wrap: wrap; }
.lc-h4 { margin: 10px 0 0; font-size: 14px; }
.lc-log { margin: 0; padding-left: 18px; display: flex; flex-direction: column; gap: 8px; font-size: 13px; line-height: 1.45; }

.lc-backdrop:has(.lc-dialog) { justify-content: center; align-items: center; padding: 16px; }
.lc-dialog { width: min(480px, 100%); display: flex; flex-direction: column; gap: 6px; padding: 22px; border-radius: 20px; background: #fff; color: var(--ink);
  box-shadow: 0 30px 70px -30px rgba(0, 0, 0, .6); animation: lc-pop .25s cubic-bezier(.2, .7, .2, 1) both; }
.lc-dialog h3 { margin: 0; font-size: 20px; }
.lc-dialog label { font-size: 13px; font-weight: 600; margin-top: 4px; }
.lc-dialog__actions { display: flex; justify-content: flex-end; gap: 8px; margin-top: 8px; }
.lc-seg { display: inline-flex; padding: 4px; border-radius: 999px; background: #f3f1ea; gap: 4px; }
.lc-seg__btn { flex: 1; border: 0; padding: 7px 12px; border-radius: 999px; background: transparent; font: inherit; font-size: 13px; cursor: pointer; }
.lc-seg__btn.is-active { background: linear-gradient(135deg, var(--gold-2), var(--gold)); font-weight: 650; }

@keyframes lc-in { from { opacity: 0; transform: translateY(6px); } }
@keyframes lc-fade { from { opacity: 0; } }
@keyframes lc-slide { from { transform: translateX(40px); opacity: 0; } }
@keyframes lc-pop { from { opacity: 0; transform: translateY(10px) scale(.98); } }
@keyframes lc-pulse { 50% { box-shadow: 0 0 0 7px rgba(220, 38, 38, .08); } }
@keyframes lc-shimmer { to { background-position: -200% 0; } }
@media (max-width: 640px) { .lc-search { min-width: 0; width: 100%; margin-left: 0; } .lc-card { grid-template-columns: auto 1fr; } .lc-card__side { grid-column: 2; text-align: left; } }
@media (prefers-reduced-motion: reduce) { .lc, .lc-drawer, .lc-dialog, .lc-backdrop, .lc-dot--red { animation: none; }
  .lc-row-enter-active, .lc-row-leave-active, .lc-row-move, .lc-card, .lc-btn { transition: none; } }
</style>
